/**
 * The COLLAPSED rail's flyout — one grammar for both hosts (#2502 §5.1, lifted
 * from the console's `apps/console/src/app/Nav.tsx` #2406 implementation).
 *
 * *"Collapsed, section items are NOT squeezed — they move into a seated
 * flyout, so the icons-only state loses no navigation."* (Organization Detail,
 * the portal design's `.rfly`). One flyout at a time, for the section under the
 * pointer or holding focus.
 *
 * The HOST renders it INSIDE its section, straight after the section's head,
 * and it positions itself `fixed` beside the rail: so Tab walks from a
 * section's mark into that section's destinations and on to the next mark —
 * the reading order of the expanded rail — and the column's scroller cannot
 * clip it. Opened by hover, focus and click (a click only ever OPENS: focus
 * arrived first); closed by leaving it, Escape (focus returns to the mark), focus
 * leaving the section, following a destination, a resize, and a new page.
 *
 * `useNavigationFlyout` is the state; `NavigationFlyout` is the drawing. The
 * console still carries its own copy of both (`Nav.tsx`) — its adoption is a
 * recorded hand-off (#2502 A1), not done here, so its specs were not moved.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { cx } from '../controls/cx'
import s from './NavigationFlyout.module.css'

export interface NavigationFlyoutItem {
  key: string
  label: ReactNode
  current?: boolean
  onSelect: () => void
}

export function useNavigationFlyout({ enabled, resetKey }: { enabled: boolean; resetKey?: unknown }) {
  const [open, setOpen] = useState<number | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchor = useRef<HTMLElement | null>(null)
  /* Escape hands focus BACK to the section's mark, and focusing a mark opens
     its flyout: without this the flyout closed and reopened in one keystroke. */
  const returning = useRef(false)
  const keep = () => { if (timer.current) clearTimeout(timer.current); timer.current = null }
  const hide = () => { keep(); setOpen(null) }
  /* 160ms, the sheet's own delay: long enough to cross the 8px gap between the
     rail and the flyout without it closing under the pointer. */
  const queueHide = () => { keep(); timer.current = setTimeout(() => setOpen(null), 160) }
  const show = (index: number, section: HTMLElement) => {
    if (!enabled || returning.current) return
    keep()
    anchor.current = section
    setOpen(index)
  }
  useEffect(() => { if (!enabled) setOpen(null) }, [enabled])
  useEffect(() => { setOpen(null) }, [resetKey])
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  useEffect(() => {
    if (open === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const section = anchor.current
      setOpen(null)
      returning.current = true
      /* The kit head's own `data-rail-head` first (#2624): a host outside the
         console does not carry the console's test id, and a flyout that closed
         without returning focus left it on an element that no longer exists.
         The test id stays as the fallback for a head the host drew itself. */
      section?.querySelector<HTMLElement>('[data-rail-head], [data-testid="nav-group-disclosure"]')?.focus()
      returning.current = false
    }
    const onResize = () => setOpen(null)
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('resize', onResize) }
  }, [open])
  return { open, show, hide, queueHide, keep, anchor }
}

export function NavigationFlyout({ id, icon, label, items, rail, anchor, onMouseEnter, onMouseLeave, onSelected }: {
  id: string
  icon?: ReactNode
  label: string
  items: readonly NavigationFlyoutItem[]
  /** The rail it stands beside — its right edge is the flyout's left. */
  rail: RefObject<HTMLElement | null>
  /** The section it belongs to — its top is the flyout's. */
  anchor: RefObject<HTMLElement | null>
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  /** Called after a destination was chosen, so the host can close it. */
  onSelected?: () => void
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  /* Beside the rail, level with its section, never off the bottom of the
     window: measured after it renders, because its height is its content's. */
  useLayoutEffect(() => {
    const panel = ref.current; const section = anchor.current; const nav = rail.current
    if (!panel || !section || !nav) return
    const top = section.getBoundingClientRect().top
    panel.style.left = `${nav.getBoundingClientRect().right + 8}px`
    panel.style.top = `${Math.max(8, Math.min(top - 4, window.innerHeight - panel.offsetHeight - 8))}px`
  })
  return (
    <div ref={ref} id={id} className={s.flyout} data-testid="nav-flyout"
      onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className={s.head} aria-hidden>
        {icon != null && <span className={s.icon}>{icon}</span>}
        {label}
      </div>
      <div className={s.body}>
        {items.map((it) => (
          <button key={it.key} type="button" className={cx(s.item, it.current && s.current)}
            aria-current={it.current ? 'page' : undefined}
            onClick={() => { it.onSelect(); onSelected?.() }}>
            {it.label}
          </button>
        ))}
      </div>
    </div>
  )
}
